import React, { useMemo, useState } from 'react';
import {Button, FormGroup, Label} from 'reactstrap';
import { FaPlus } from 'react-icons/fa';
import { useBoolean } from 'usehooks-ts';
import Select from 'react-select';
import {MenuMasterFormModal, MenuMasterTable } from './components';
import {CompanyMaster} from "../../models/companymaster/CompanyMaster";
import axios, { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import {useCompanyMaster} from "../../queries";
import { useAddMenuMaster, useDeleteMenuMaster, useMenuMaster, useUpdateMenuMaster } from "./queries";
import {MenuMaster} from "../../models/config/MenuMaster";
import Swal from "sweetalert2";
import {extractAccessToken} from "../../helpers/jwt";

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const LegacyMenuMasterConfig = () => {
    const queryClient = useQueryClient();
    
    const { data: companyMaster } = useCompanyMaster();
    const companyOptions = useMemo(() => {
        if (!companyMaster) {
            return undefined;
        }

        const options: CompanyMaster[] = [
            {
                sid: 'all',
                company_name: 'เลือกทั้งหมด'
            } as CompanyMaster
        ];

        return options.concat(companyMaster);
    }, [companyMaster]);
    const companySIDSet = useMemo(
        () =>
            companyMaster
                ? new Set(companyMaster.map(company => company.sid))
                : undefined,
        [companyMaster]
    );

    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();

    const { data: menuMaster, isFetching: isFetchingMenuMaster } =
        useMenuMaster();
    const filteredMenuMaster = useMemo(
        () =>
            menuMaster?.filter(menu => {
                if (!selectedCompany || selectedCompany.sid === 'all') {
                    return true;
                }

                return menu.sid === selectedCompany.sid;
            }),
        [menuMaster, selectedCompany]
    );
    
    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addMenuMaster } = useAddMenuMaster();

    const [selectedEditMenuMasters, setSelectedEditMenuMasters] =
        useState<MenuMaster[]>();
    const isOpenEditModal = !!selectedEditMenuMasters;
    const { mutate: updateMenuMaster } = useUpdateMenuMaster();

    const { mutate: deleteMenuMaster } = useDeleteMenuMaster();

    const handleSubmitAddMenuMaster = (menuMasters: MenuMaster[]) => {
        const requests = menuMasters.map(menu => ({
            ...menu,
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        }));

        console.log('ngame add requests', requests);

        requests.forEach(request =>
            addMenuMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    toggleIsOpenCreateModal();

                    await queryClient.invalidateQueries({
                        queryKey: ['menu-master']
                    });
                }
            })
        );
    };

    const handleEditMenuMasters = (menuMasters: MenuMaster[]) => {
        setSelectedEditMenuMasters(menuMasters);
    };

    const handleCloseEditModal = () => {
        setSelectedEditMenuMasters(undefined);
    };

    const handleSubmitEditMenuMaster = async (menuMasters: MenuMaster[]) => {
        if (menuMasters.length === 0 || !filteredMenuMaster) {
            return;
        }

        // Look-ups
        const menuMasterSIDSet = new Set(menuMasters.map(menu => menu.sid));
        const currentMenus = filteredMenuMaster.filter(
            menu => menu.id === menuMasters[0].id
        );
        const currentMenuSIDSet = new Set(currentMenus.map(menu => menu.sid));

        // Delete requests
        const deleteMenuRequests = currentMenus
            .filter(existingMenu => !menuMasterSIDSet.has(existingMenu.sid))
            .map(menu => ({ sid: menu.sid, id: menu.id }));
        const deleteMenuSIDSet = new Set(
            deleteMenuRequests.map(menu => menu.sid)
        );

        // Add requests
        const addMenuRequests = menuMasters
            .filter(menu => !currentMenuSIDSet.has(menu.sid))
            .map(menu => ({
                ...menu,
                created_by:
                    extractAccessToken().citizen_id ||
                    extractAccessToken().username
            }));
        const addMenuSIDSet = new Set(addMenuRequests.map(menu => menu.sid));

        // Edit requests
        const editMenuRequests = menuMasters
            .filter(
                menu =>
                    !deleteMenuSIDSet.has(menu.sid) &&
                    !addMenuSIDSet.has(menu.sid)
            )
            .map(menu => ({
                ...menu,
                updated_by:
                    extractAccessToken().citizen_id ||
                    extractAccessToken().username
            }));

        try {
            await Promise.all([
                ...deleteMenuRequests.map(request => deleteMenuMaster(request)),
                ...addMenuRequests.map(request => addMenuMaster(request)),
                ...editMenuRequests.map(request => updateMenuMaster(request))
            ]);

            handleCloseEditModal();

            await Swal.fire({
                title: 'Success!',
                icon: 'success'
            });
            await queryClient.invalidateQueries({ queryKey: ['menu-master'] });
        } catch (error) {
            await Swal.fire({
                title: 'Error!',
                icon: 'error',
                text: getErrorMessage(error)
            });
        }
    };

    const handleDeleteMenuMaster = async (menuMasters: MenuMaster[]) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${menuMasters[0]?.id}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }

        const requests = menuMasters.map(menuMaster => ({
            sid: menuMaster.sid,
            id: menuMaster.id
        }));

        console.log('ngame delete requests', requests);

        requests.forEach(request =>
            deleteMenuMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: ['menu-master']
                    });
                }
            })
        );
    };
    
    return (
        <div className="master-data-content">
            <div className="tw-flex tw-justify-between">
                <div className="trapezoid-head tw-w-[200px]">
                    <span>Workflow Menu Master Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="tw-flex tw-items-center tw-gap-1 tw-self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>

            <div className="mat-box">
                <div className="tw-flex tw-justify-between">
                    <div className="trapezoid-head tw-w-fit">
                        <span>Search</span>
                    </div>
                </div>
                <div className="mat-box tw-flex tw-flex-col">
                    <FormGroup className="w-full md:w-1/3">
                        <Label for="sid">Company</Label>
                        <Select
                            options={companyOptions}
                            value={selectedCompany}
                            getOptionLabel={(company: CompanyMaster) =>
                                company.company_name
                            }
                            getOptionValue={(company: CompanyMaster) =>
                                company.sid
                            }
                            onChange={setSelectedCompany}
                        />
                    </FormGroup>
                </div>

                <MenuMasterTable
                    isPending={isFetchingMenuMaster}
                    menus={filteredMenuMaster}
                    onEdit={handleEditMenuMasters}
                    onDelete={handleDeleteMenuMaster}
                />

                {/* Create modal */}
                <MenuMasterFormModal
                    title="Create Menu ID"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddMenuMaster}
                />

                {/* Edit modal */}
                <MenuMasterFormModal
                    disabledKeyField
                    title="Edit Menu ID"
                    defaultValues={selectedEditMenuMasters}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditMenuMaster}
                />
            </div>
        </div>
    );
}